import React, { useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";

export const EmailReportButton = ({ pdfPath, filename, subject, module, summary = "", testid = "email-report", variant = "solid", className = "", beforeSend }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (beforeSend) await beforeSend();
      const { data: blob } = await api.get(pdfPath, { responseType: "blob" });
      const fd = new FormData();
      fd.append("to", to.trim());
      fd.append("subject", subject);
      fd.append("module", module || "");
      fd.append("summary", summary || "");
      fd.append("pdf", new File([blob], filename, { type: "application/pdf" }));
      const { data } = await api.post("/reports/email-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${t("rep.sent")} ${data.to}`);
      setOpen(false);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const cls = variant === "outline"
    ? "flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#044CBC] text-[#044CBC] text-xs font-bold hover:bg-blue-50 transition-colors"
    : "flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#033A8C] text-white text-xs font-bold hover:bg-[#16335F] transition-colors";

  return (
    <>
      <button type="button" onClick={() => { setTo(user?.email || ""); setOpen(true); }} data-testid={`${testid}-button`} className={`${cls} ${className}`}>
        <Mail className="w-3.5 h-3.5" /> {t("rep.send")}
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" data-testid={`${testid}-modal`} onClick={() => !busy && setOpen(false)}>
          <form onSubmit={send} className="ec-card p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2"><Mail className="w-5 h-5 text-[#044CBC]" /> {t("rep.title")}</h3>
            <p className="text-xs text-slate-500">{t("rep.help")} <b>{filename}</b></p>
            <div>
              <label className="ec-label">{t("rep.to")}</label>
              <input className="ec-input" type="email" value={to} onChange={(e) => setTo(e.target.value)} required autoFocus data-testid={`${testid}-to`} />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={busy} data-testid={`${testid}-confirm`} className="flex-1 px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("rep.confirm")}
              </button>
              <button type="button" onClick={() => setOpen(false)} disabled={busy} data-testid={`${testid}-cancel`} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">{t("u.cancel")}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
